/**
 * Validación de inventario por proyecto (carril web).
 *
 * Responde, ANTES de pedir nada: "¿qué necesito, qué tengo, DÓNDE está y qué me
 * falta comprar?". Es el paso previo, read-only, del proceso de abastecimiento.
 *
 * ## Por qué este módulo no calcula nada
 *
 * El cálculo (match contra el catálogo, rollup por bodega, faltante, agrupación
 * por alcance, resolución de unidad) vive en UN solo lugar:
 * `mcp-server/src/tools/reconcileMaterialsTools.js#checkMaterialsAvailability`,
 * que a su vez reusa `scoreMatch` de match_inventory_items y
 * `rollupStockLocations` de query_inventory. Esta capa sólo arma la lista y la
 * manda por `callMcpTool` — el mismo camino que usa el agente.
 *
 * Reimplementar el match/rollup/shortfall del lado del cliente sería una SEGUNDA
 * copia de una regla de negocio, o sea la deriva de #353/#454 que documenta
 * CLAUDE.md ("Lane Parity"): los dos carriles empiezan iguales y divergen al
 * primer cambio. `materialsAvailabilityService.test.js` asserta que este módulo
 * NO toca supabase para calcular disponibilidad, justamente para que esa copia
 * no pueda nacer sin que un test se ponga rojo.
 *
 * ## De dónde sale la lista de materiales
 *
 * La tool exige que el caller le pase `items`; `project_id` es sólo
 * trazabilidad. La fuente elegida es `quotation_items` de la cotización
 * APROBADA del proyecto: es la única fuente que ya existe en la DB con
 * cantidades por proyecto, y es lo que se le comprometió al cliente.
 * La lista es un PUNTO DE PARTIDA editable, no un contrato: la pantalla deja
 * agregar, quitar y ajustar cantidades antes de consultar.
 *
 * Un proyecto sin cotización aprobada NO bloquea la pantalla: arranca vacía y
 * el usuario carga los ítems a mano.
 *
 * @module services/materialsAvailabilityService
 */

import { supabase } from './supabase';
import { callMcpTool } from './mcpClient';
// #717 — orden canonico de las lineas de una cotizacion.
import { ordenarLineas } from './ordenDeLineas';

/** Estados de cotización que cuentan como "la lista comprometida del proyecto". */
const APPROVED_STATUSES = ['approved', 'accepted'];

/**
 * Etiquetas legibles de los tipos de línea que NO se reconcilian contra
 * inventario. Se muestran al usuario, así que van en español y sin el valor
 * crudo de la columna.
 */
const NON_MATERIAL_LABELS = {
  labor: 'mano de obra',
  tool: 'herramienta',
  admin: 'administrativo',
  other: 'otro',
};

/**
 * Materiales de la cotización aprobada del proyecto, listos para reconciliar.
 *
 * Las líneas que no son `material` se EXCLUYEN pero NO en silencio: se devuelven
 * en `excluded` para que la pantalla las muestre. El motivo es concreto: mandar
 * "Instalación y calibración, 4 horas" contra el catálogo de inventario vuelve
 * como `unmatched` con `qty_to_purchase: 4`, o sea la pantalla le diría al
 * comprador que hay que comprar 4 horas. Medido en prod: de 32 quotation_items,
 * 25 son `material`, 6 `labor` y 1 `tool`.
 *
 * @param {string} projectId
 * @returns {Promise<{data: {quotation: Object|null, items: Array, excluded: Array}|null, error: Error|null}>}
 */
export const getProjectMaterialList = async (projectId) => {
  try {
    // #717 — el orden del EMBED se aplica apenas creado el builder, y no al
    // final, para que no se confunda con el `.order('created_at')` de abajo:
    // ése es del PADRE (elegir la cotización aprobada más reciente) y no toca
    // las líneas. Son dos órdenes distintos sobre la misma consulta.
    const { data, error } = await ordenarLineas(
      supabase
        .from('quotations')
        .select(`
          id,
          quotation_number,
          status,
          created_at,
          quotation_items(id, description, item_type, quantity, unit, sort_order)
        `)
    )
      .eq('project_id', projectId)
      .in('status', APPROVED_STATUSES)
      .order('created_at', { ascending: false })
      .limit(1);

    if (error) throw error;

    const quotation = (data || [])[0] || null;

    if (!quotation) {
      // Decisión de producto: sin cotización aprobada la pantalla arranca vacía
      // y el usuario carga a mano. NO es un error ni un estado bloqueante.
      return { data: { quotation: null, items: [], excluded: [] }, error: null };
    }

    const lines = [...(quotation.quotation_items || [])].sort(
      (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)
    );

    const items = [];
    const excluded = [];

    lines.forEach((line) => {
      const name = (line.description || '').trim();
      if (!name) return;

      if (line.item_type !== 'material') {
        excluded.push({
          name,
          item_type: line.item_type,
          label: NON_MATERIAL_LABELS[line.item_type] || line.item_type,
        });
        return;
      }

      items.push({
        name,
        quantity: Number(line.quantity) || 0,
        unit: (line.unit || '').trim(),
        // `quotation_items` no tiene columna de alcance; el eje de agrupación lo
        // pone el usuario en la pantalla si lo necesita.
        alcance: '',
      });
    });

    return {
      data: {
        quotation: {
          id: quotation.id,
          quotation_number: quotation.quotation_number,
          status: quotation.status,
          created_at: quotation.created_at,
        },
        items,
        excluded,
      },
      error: null,
    };
  } catch (error) {
    console.error('Get project material list error:', error);
    return { data: null, error };
  }
};

/**
 * Normaliza una fila editable al shape que exige la tool.
 *
 * #356 — `unit` y `alcance` vacíos se OMITEN, no se mandan como ''. El servidor
 * resuelve la unidad desde el catálogo del ítem que matcheó (un hecho suyo) y,
 * si tampoco la tiene, la marca como ambigua. Mandar '' la haría pasar por
 * "la dijo el usuario" y esa unidad inventada viajaría hasta la orden de compra
 * con cara de dato.
 */
const toToolItem = (row) => {
  const item = {
    name: (row.name || '').trim(),
    quantity: Number(row.quantity),
  };
  const unit = typeof row.unit === 'string' ? row.unit.trim() : '';
  if (unit) item.unit = unit;
  const alcance = typeof row.alcance === 'string' ? row.alcance.trim() : '';
  if (alcance) item.alcance = alcance;
  return item;
};

/**
 * Consulta disponibilidad contra el catálogo de inventario (READ-ONLY).
 *
 * NO consume stock, NO crea material_requests, NO crea órdenes de compra.
 * Delega enteramente en la tool `check_materials_availability`.
 *
 * @param {Object} params
 * @param {string} [params.projectId] - trazabilidad; la tool no lo usa para armar la lista
 * @param {Array<{name: string, quantity: number, unit?: string, alcance?: string}>} params.items
 * @returns {Promise<{data: Object|null, message: string|null, error: Error|null}>}
 */
export const checkMaterialsAvailability = async ({ projectId, items } = {}) => {
  const rows = (Array.isArray(items) ? items : [])
    .filter((row) => (row?.name || '').trim() && Number(row?.quantity) > 0)
    .map(toToolItem);

  if (rows.length === 0) {
    // La tool exige un array no vacío; parar acá evita un round-trip que sólo
    // puede volver con un ValidationError del servidor.
    return {
      data: null,
      message: null,
      error: new Error('Agregá al menos un material con cantidad para consultar disponibilidad'),
    };
  }

  const { data, message, error } = await callMcpTool('check_materials_availability', {
    project_id: projectId,
    items: rows,
  });

  if (error) return { data: null, message: null, error };

  return { data, message: message || null, error: null };
};

const materialsAvailabilityService = {
  getProjectMaterialList,
  checkMaterialsAvailability,
};

export default materialsAvailabilityService;
